import { Fragment, useEffect, useState } from 'react';
import { formatarCno, formatarDocumento, tipoDocumento } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { paraApi, useFiltro } from '../estado/filtros.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { DetalheDocumento } from '../componentes/DetalheDocumento.js';
import { competencia as fmtCompetencia, data, inteiro, moeda, moedaOuTraco } from '../lib/formato.js';
import { nomeArquivo, urlExportacao } from '../lib/exportar.js';
import type { CampoOrdem, DocumentoResumo } from '../lib/tipos.js';

const POR_PAGINA = 50;
const COLUNAS = 16;

/** Campos que ordenam por texto/data começam ascendentes; dinheiro começa do maior. */
const DIRECAO_INICIAL: Record<CampoOrdem, 'asc' | 'desc'> = {
  dataEmissao: 'desc',
  emitenteNome: 'asc',
  valorTotal: 'desc',
  icms: 'desc',
  iss: 'desc',
  retIss: 'desc',
  retInss: 'desc',
};

function Ordenavel({
  campo,
  rotulo,
  ordem,
  aoOrdenar,
  className,
}: {
  campo: CampoOrdem;
  rotulo: string;
  ordem: { campo: CampoOrdem; direcao: 'asc' | 'desc' };
  aoOrdenar: (campo: CampoOrdem) => void;
  className?: string;
}) {
  const ativo = ordem.campo === campo;
  return (
    <th
      className={`sortable ${className ?? ''}`}
      aria-sort={ativo ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" onClick={() => aoOrdenar(campo)}>
        {rotulo}
        {ativo ? <span className="arw">{ordem.direcao === 'asc' ? '▲' : '▼'}</span> : null}
      </button>
    </th>
  );
}

function Severidade({ doc }: { doc: DocumentoResumo }) {
  if (doc.alertas === 0) return <span className="badge ok">ok</span>;
  const classe = doc.severidadeMaxima ?? 'tipo';
  return <span className={`badge ${classe}`}>{doc.alertas}</span>;
}

export function Documentos() {
  const { filtro, definir, alternarTipo } = useFiltro();
  const [busca, setBusca] = useState(filtro.busca);
  const [pagina, setPagina] = useState(0);
  const [ordem, setOrdem] = useState<{ campo: CampoOrdem; direcao: 'asc' | 'desc' }>({
    campo: 'dataEmissao',
    direcao: 'desc',
  });
  const [aberto, setAberto] = useState<string | null>(null);

  // A busca é aplicada com folga para não disparar uma query por tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      if (busca !== filtro.busca) {
        definir({ busca });
        setPagina(0);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [busca, filtro.busca, definir]);

  const competencias = trpc.documentos.competencias.useQuery();
  const consulta = trpc.documentos.listar.useQuery({
    filtro: paraApi(filtro),
    ordem,
    pagina,
    porPagina: POR_PAGINA,
  });

  function ordenarPor(campo: CampoOrdem) {
    setPagina(0);
    setOrdem((atual) =>
      atual.campo === campo
        ? { campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: DIRECAO_INICIAL[campo] },
    );
  }

  const total = consulta.data?.total ?? 0;
  const itens = consulta.data?.itens ?? [];
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  return (
    <section className="panel">
      <div className="toolbar">
        <div className="search">
          <label htmlFor="busca" style={{ position: 'absolute', left: '-9999px' }}>
            Buscar documentos
          </label>
          <input
            id="busca"
            type="search"
            placeholder="Buscar por número, CNPJ, nome, chave, CNO ou descrição"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <label htmlFor="competencia" style={{ position: 'absolute', left: '-9999px' }}>
          Competência
        </label>
        <select
          id="competencia"
          value={filtro.competencia ?? ''}
          onChange={(e) => {
            definir({ competencia: e.target.value === '' ? undefined : e.target.value });
            setPagina(0);
          }}
        >
          <option value="">Todas as competências</option>
          {(competencias.data ?? []).map((c) => (
            <option key={c} value={c}>
              {fmtCompetencia(c)}
            </option>
          ))}
        </select>

        <div className="filters">
          {tipoDocumento.options.map((tipo) => (
            <button
              key={tipo}
              type="button"
              className="fchip"
              aria-pressed={filtro.tipos.includes(tipo)}
              onClick={() => {
                alternarTipo(tipo);
                setPagina(0);
              }}
            >
              {tipo}
            </button>
          ))}
          <button
            type="button"
            className="fchip"
            aria-pressed={filtro.somenteComCno}
            onClick={() => {
              definir({ somenteComCno: !filtro.somenteComCno });
              setPagina(0);
            }}
          >
            Só com CNO
          </button>
        </div>

        <a
          className="btn sm"
          href={urlExportacao('documentos', filtro)}
          download={nomeArquivo('documentos')}
        >
          Exportar documentos
        </a>
        <a className="btn sm" href={urlExportacao('itens', filtro)} download={nomeArquivo('itens')}>
          Exportar itens
        </a>
      </div>

      {consulta.error ? <Erro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}

      {consulta.isPending ? <Carregando linhas={8} rotulo="Carregando os documentos" /> : null}

      {!consulta.isPending && !consulta.error && itens.length === 0 ? (
        <Vazio titulo="Nenhum documento com esses filtros">
          Ajuste a busca, a competência ou os tipos selecionados.
        </Vazio>
      ) : null}

      {itens.length > 0 ? (
        <>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th className="r">Nº</th>
                  <Ordenavel
                    campo="dataEmissao"
                    rotulo="Emissão"
                    ordem={ordem}
                    aoOrdenar={ordenarPor}
                    className="nowrap"
                  />
                  <Ordenavel campo="emitenteNome" rotulo="Emitente" ordem={ordem} aoOrdenar={ordenarPor} />
                  <th>Tomador / dest.</th>
                  <th>CNO</th>
                  <Ordenavel campo="valorTotal" rotulo="Valor" ordem={ordem} aoOrdenar={ordenarPor} className="r" />
                  <Ordenavel campo="icms" rotulo="ICMS" ordem={ordem} aoOrdenar={ordenarPor} className="r" />
                  <th className="r">PIS</th>
                  <th className="r">COFINS</th>
                  <th className="r">IBS</th>
                  <th className="r">CBS</th>
                  <Ordenavel campo="iss" rotulo="ISS" ordem={ordem} aoOrdenar={ordenarPor} className="r" />
                  <Ordenavel campo="retIss" rotulo="ISS ret." ordem={ordem} aoOrdenar={ordenarPor} className="r" />
                  <Ordenavel campo="retInss" rotulo="INSS ret." ordem={ordem} aoOrdenar={ordenarPor} className="r" />
                  <th className="r">Div.</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((doc) => (
                  <Fragment key={doc.id}>
                    <tr
                      className="expander"
                      tabIndex={0}
                      aria-expanded={aberto === doc.id}
                      onClick={() => setAberto(aberto === doc.id ? null : doc.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setAberto(aberto === doc.id ? null : doc.id);
                        }
                      }}
                    >
                      <td>
                        <span className="badge tipo">{doc.tipo}</span>
                      </td>
                      <td className="num r">
                        {doc.numero || '—'}
                        {doc.serie ? <span className="muted">/{doc.serie}</span> : null}
                      </td>
                      <td className="num nowrap">{data(doc.dataEmissao)}</td>
                      <td>
                        <span className="trunc" title={doc.emitente.nome}>
                          {doc.emitente.nome || '—'}
                        </span>
                        <span className="muted mono" style={{ fontSize: 10.5 }}>
                          {formatarDocumento(doc.emitente.doc)}
                        </span>
                      </td>
                      <td>
                        <span className="trunc" title={doc.destinatario.nome}>
                          {doc.destinatario.nome || '—'}
                        </span>
                        <span className="muted mono" style={{ fontSize: 10.5 }}>
                          {formatarDocumento(doc.destinatario.doc)}
                        </span>
                      </td>
                      <td>
                        {doc.cnos.length > 0 ? (
                          doc.cnos.map((c) => (
                            <span className="badge cno" key={c}>
                              {formatarCno(c)}
                            </span>
                          ))
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="num r strong">{moeda(doc.valorTotal)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.icms)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.pis)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.cofins)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.ibs)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.cbs)}</td>
                      <td className="num r">{moedaOuTraco(doc.tributos.iss)}</td>
                      <td className="num r">{moedaOuTraco(doc.retencoes.iss)}</td>
                      <td className="num r">{moedaOuTraco(doc.retencoes.inss)}</td>
                      <td className="r">
                        <Severidade doc={doc} />
                      </td>
                    </tr>
                    {aberto === doc.id ? (
                      <tr className="detail">
                        <td colSpan={COLUNAS}>
                          <div className="inner">
                            <DetalheDocumento id={doc.id} />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pag">
            <span>
              {inteiro(total)} documento(s) · página {inteiro(pagina + 1)} de {inteiro(ultimaPagina + 1)}
            </span>
            <button
              type="button"
              className="btn sm"
              disabled={pagina === 0 || consulta.isFetching}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              className="btn sm"
              disabled={pagina >= ultimaPagina || consulta.isFetching}
              onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
            >
              Próxima
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
